import { trackAction } from "../index";

/**
 * Page lifecycle marker (web).
 *
 * A Chromium renderer crash, or a Memory Saver / Sleeping Tabs discard, leaves
 * no RUM event behind: the JS context dies, nothing flushes, and the document
 * that replaces it starts an ordinary page load. This module makes the death
 * visible from the other side.
 *
 * Every document keeps a small record in `sessionStorage` (current path, last
 * click, latest heap sample, timestamps) and marks it clean on `pagehide`. A
 * document that boots and finds the previous record NOT marked clean reports
 * one RUM action, `page_restart`, carrying what the previous document was
 * doing, how long it lived, its last heap sample, and how this document was
 * started: a browser reload, a discard, or a navigation. It is an action, not
 * an error: a restart is a signal to count, not a defect in this document. A
 * user's own refresh unloads cleanly and reports the action
 * `page_restart_clean` instead, which is the denominator for "deaths per
 * reload".
 *
 * `sessionStorage` is per tab, so the record follows the tab across reloads
 * and never leaks between tabs. The one exception is a window opened with
 * `window.open` (the Auth0 sign-in popup): the browser copies the opener's
 * `sessionStorage` into it, so the popup would read the opener's live record
 * as a dead predecessor. A document with a `window.opener` therefore reports
 * nothing about a predecessor. Known false negative: a tab the app opens of
 * itself with `window.open` loses its restart signal.
 *
 * The same module samples `performance.memory` once a minute into the RUM
 * action `memory_sample`, so heap size and the machine's heap limit become
 * visible per tenant and per machine. Those fields are Chromium-only and
 * optional: Safari and Firefox still send the sample without them.
 *
 * Cost: one small synchronous storage write every five seconds, one RUM
 * action a minute, three property reads at boot. Nothing here reads the DOM
 * beyond the element that was clicked.
 *
 * Installed once from `index.web.js`, before the app registers. Every entry
 * point is wrapped so a storage or API failure can never break boot.
 */

export const PAGE_LIFECYCLE_STORAGE_KEY = "dapple_page_lifecycle";
export const RESTART_ACTION = "page_restart";
export const CLEAN_RESTART_ACTION = "page_restart_clean";
export const MEMORY_SAMPLE_ACTION = "memory_sample";

export const RECORD_INTERVAL_MS = 5_000;
export const MEMORY_SAMPLE_INTERVAL_MS = 60_000;

const MAX_CLICK_LABEL_LENGTH = 60;
const MAX_CLICK_ANCESTORS = 5;
// A calendar day button ("23") and the book's date header ("Wed 23 Sep 2026"):
// the controls most deaths follow, and neither says anything about a person.
const DAY_NUMBER = /^\d{1,2}$/;
const BOOK_DATE = /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun) \d{2} [A-Z][a-z]{2} \d{4}$/;

// The app's own UI strings, handed over by the app once its locale has loaded
// (registerUiLabels). Clicked text is kept only when it is one of these.
let knownUiLabels: ReadonlySet<string> | null = null;

const normaliseText = (text: string | null | undefined) =>
  text?.replace(/\s+/g, " ").trim() ?? "";

/**
 * Registers the strings the app itself renders (the active locale's values),
 * so a click on "Today" or "Cancel Appointment" can be reported by its text
 * while a click on anything carrying clinic data cannot. Called again when the
 * language changes; each call replaces the previous set.
 * @param values - Every UI string of the active locale
 */
export const registerUiLabels = (values: Iterable<string>) => {
  const labels = new Set<string>();
  for (const value of values) {
    const label = normaliseText(value);
    if (label && label.length <= MAX_CLICK_LABEL_LENGTH) labels.add(label);
  }
  knownUiLabels = labels;
};

/** Whether clicked text is safe to report: the app's own words, a day or a date. */
const isUiText = (text: string) =>
  DAY_NUMBER.test(text) || BOOK_DATE.test(text) || !!knownUiLabels?.has(text);

/** What one document leaves behind for the next one to read. */
export type LifecycleRecord = {
  startedAt: number;
  updatedAt: number;
  cleanExit: boolean;
  view?: string;
  hidden?: boolean;
  lastClick?: string;
  lastClickAt?: number;
  heapUsedMb?: number;
  heapLimitMb?: number;
};

type ChromiumMemory = {
  usedJSHeapSize: number;
  jsHeapSizeLimit: number;
};

type Timers = {
  record: ReturnType<typeof setInterval> | null;
  memory: ReturnType<typeof setInterval> | null;
};

let installed = false;
let record: LifecycleRecord | null = null;
const timers: Timers = { record: null, memory: null };
const listeners: Array<() => void> = [];

const toMb = (bytes: number) => Math.round(bytes / 1_048_576);

const readStorage = (): LifecycleRecord | null => {
  try {
    const raw = window.sessionStorage.getItem(PAGE_LIFECYCLE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LifecycleRecord>;
    if (typeof parsed?.startedAt !== "number") return null;
    return parsed as LifecycleRecord;
  } catch {
    return null;
  }
};

const writeStorage = (value: LifecycleRecord) => {
  try {
    window.sessionStorage.setItem(
      PAGE_LIFECYCLE_STORAGE_KEY,
      JSON.stringify(value),
    );
  } catch {
    // Private mode or blocked storage: the marker is best effort.
  }
};

/** Chromium's `performance.memory`, or nothing on other engines. */
const readMemory = (): Pick<LifecycleRecord, "heapUsedMb" | "heapLimitMb"> => {
  try {
    const memory = (performance as Performance & { memory?: ChromiumMemory })
      .memory;
    if (!memory) return {};
    return {
      heapUsedMb: toMb(memory.usedJSHeapSize),
      heapLimitMb: toMb(memory.jsHeapSizeLimit),
    };
  } catch {
    return {};
  }
};

/** Chromium's `navigator.deviceMemory` in GB (bucketed, capped at 8), if any. */
const readDeviceMemoryGb = (): number | undefined => {
  try {
    const value = (navigator as Navigator & { deviceMemory?: number })
      .deviceMemory;
    return typeof value === "number" ? value : undefined;
  } catch {
    return undefined;
  }
};

/** How this document was started, from the Navigation Timing entry. */
const readNavigationType = (): string => {
  try {
    const entry = performance.getEntriesByType?.("navigation")?.[0] as
      | PerformanceNavigationTiming
      | undefined;
    return entry?.type ?? "unknown";
  } catch {
    return "unknown";
  }
};

/** True when the browser discarded the previous document and reloaded it. */
const readWasDiscarded = (): boolean => {
  try {
    return (document as Document & { wasDiscarded?: boolean }).wasDiscarded === true;
  } catch {
    return false;
  }
};

const currentPath = () => {
  try {
    return window.location.pathname;
  } catch {
    return undefined;
  }
};

const isHidden = () => {
  try {
    return document.visibilityState === "hidden";
  } catch {
    return false;
  }
};

/**
 * True when another window opened this one (the sign-in popup). A read that
 * throws counts as no opener, so the marker reports as it would without the
 * guard rather than going quiet.
 */
const hasOpener = () => {
  try {
    return window.opener != null;
  } catch {
    return false;
  }
};

/**
 * A short label for a clicked element that never carries clinic data.
 *
 * Walking up from the clicked element, the first of: a `data-testid` (what
 * React Native's `testID` renders on web), a `data-dd-action-name` (Datadog's
 * opt-in action label), or the element's text when that text is one of the
 * app's own UI strings, a day number or a book date (`isUiText`). Otherwise the
 * clicked element's role or tag. Free text is never reported: appointment
 * cards, message threads and notes put patient names, treatments and message
 * contents on screen, and this value is written to sessionStorage and sent to
 * RUM. `aria-label` is never read, for the same reason.
 */
export const labelForClick = (target: EventTarget | null): string | undefined => {
  const clicked = target instanceof Element ? target : null;
  let node = clicked;
  let textMayBeLabel = true;
  for (let depth = 0; node && depth < MAX_CLICK_ANCESTORS; depth++) {
    const attribute =
      node.getAttribute("data-testid") ??
      node.getAttribute("data-dd-action-name");
    if (attribute) return attribute.slice(0, MAX_CLICK_LABEL_LENGTH);
    if (textMayBeLabel) {
      const text = normaliseText(node.textContent);
      // An ancestor's text only grows, so once it is longer than any label
      // could be, stop reading text (a click on a day column's background
      // would otherwise read the whole column) but keep looking for a test id.
      if (text.length > MAX_CLICK_LABEL_LENGTH) textMayBeLabel = false;
      else if (text && isUiText(text)) return text;
    }
    node = node.parentElement;
  }
  if (!clicked) return undefined;
  const role = clicked.getAttribute("role");
  return role ? `role:${role}` : clicked.tagName.toLowerCase();
};

const updateRecord = (patch: Partial<LifecycleRecord> = {}) => {
  if (!record) return;
  record = {
    ...record,
    ...readMemory(),
    view: currentPath(),
    hidden: isHidden(),
    updatedAt: Date.now(),
    ...patch,
  };
  writeStorage(record);
};

const sampleMemory = (phase: "boot" | "interval") => {
  const memory = readMemory();
  trackAction(MEMORY_SAMPLE_ACTION, {
    phase,
    ...memory,
    deviceMemoryGb: readDeviceMemoryGb(),
    view: currentPath(),
    hidden: isHidden(),
    pageAgeMs: record ? Date.now() - record.startedAt : undefined,
  });
};

/**
 * Reports what happened to the previous document in this tab, if anything
 * did. Exported for tests; `installPageLifecycleMarker` calls it at boot.
 */
export const reportPreviousDocument = (
  previous: LifecycleRecord | null,
  now: number,
) => {
  if (!previous) return;

  const navigationType = readNavigationType();
  const lifetimeMs = Math.max(0, previous.updatedAt - previous.startedAt);

  if (previous.cleanExit) {
    trackAction(CLEAN_RESTART_ACTION, {
      navigationType,
      previousView: previous.view,
      previousLifetimeMs: lifetimeMs,
    });
    return;
  }

  const discarded = readWasDiscarded();
  trackAction(RESTART_ACTION, {
    reason: discarded ? "discarded" : navigationType,
    navigationType,
    discarded,
    initialVisibility: isHidden() ? "hidden" : "visible",
    previousView: previous.view,
    previousHidden: previous.hidden,
    previousLastClick: previous.lastClick,
    previousLastClickAgoMs:
      previous.lastClickAt !== undefined
        ? Math.max(0, previous.updatedAt - previous.lastClickAt)
        : undefined,
    previousLifetimeMs: lifetimeMs,
    previousLastSeenAgoMs: Math.max(0, now - previous.updatedAt),
    previousHeapUsedMb: previous.heapUsedMb,
    previousHeapLimitMb: previous.heapLimitMb,
    deviceMemoryGb: readDeviceMemoryGb(),
  });
};

const listen = <K extends keyof WindowEventMap>(
  type: K,
  handler: (event: WindowEventMap[K]) => void,
  options?: AddEventListenerOptions,
) => {
  window.addEventListener(type, handler, options);
  listeners.push(() => window.removeEventListener(type, handler, options));
};

const listenDocument = <K extends keyof DocumentEventMap>(
  type: K,
  handler: (event: DocumentEventMap[K]) => void,
  options?: AddEventListenerOptions,
) => {
  document.addEventListener(type, handler, options);
  listeners.push(() =>
    document.removeEventListener(type, handler, options),
  );
};

/**
 * Installs the marker for this document. Idempotent. Never throws: a boot
 * must not fail because telemetry could not be set up.
 */
export const installPageLifecycleMarker = () => {
  if (installed) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  installed = true;

  try {
    const now = Date.now();
    // A popup's sessionStorage is a copy of its opener's, so the record it
    // finds is the opener's live one, not a predecessor of its own.
    if (!hasOpener()) reportPreviousDocument(readStorage(), now);

    record = {
      startedAt: now,
      updatedAt: now,
      cleanExit: false,
      view: currentPath(),
      hidden: isHidden(),
      ...readMemory(),
    };
    writeStorage(record);

    // A clean unload. `pagehide` is the reliable one on Chromium and fires for
    // reloads, navigations and tab closes; `beforeunload` is the fallback.
    const markClean = () => updateRecord({ cleanExit: true });
    listen("pagehide", markClean);
    listen("beforeunload", markClean);

    // Restored from the back/forward cache: the document is alive again and
    // its next exit has to be observed afresh.
    listen("pageshow", event => {
      if (event.persisted) updateRecord({ cleanExit: false });
    });

    listenDocument("visibilitychange", () => updateRecord());

    listenDocument(
      "click",
      event => {
        const label = labelForClick(event.target);
        if (label) updateRecord({ lastClick: label, lastClickAt: Date.now() });
      },
      { capture: true, passive: true },
    );

    timers.record = setInterval(() => updateRecord(), RECORD_INTERVAL_MS);
    timers.memory = setInterval(
      () => sampleMemory("interval"),
      MEMORY_SAMPLE_INTERVAL_MS,
    );

    sampleMemory("boot");
  } catch {
    // Telemetry only. Leave whatever was installed in place and carry on.
  }
};

/** Tears everything down so a test can boot a fresh "document". */
export const resetPageLifecycleMarkerForTests = () => {
  for (const remove of listeners.splice(0)) remove();
  if (timers.record) clearInterval(timers.record);
  if (timers.memory) clearInterval(timers.memory);
  timers.record = null;
  timers.memory = null;
  record = null;
  installed = false;
  knownUiLabels = null;
};
